import { Head, Link, router } from "@inertiajs/react"
import { ChevronDown, CirclePlus } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Contact {
  id: number
  display_name: string
  salutation: string | null
  first_name: string | null
  middle_name: string | null
  last_name: string | null
  suffix: string | null
  department: string | null
  phone: string | null
  cell_phone: string | null
  home_phone: string | null
  fax: string | null
  email: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  notes: string | null
  family_info: string | null
  birth_month: number | null
  birth_day: number | null
  send_birthday_card: boolean
  sales_rep_id: number | null
  sales_rep_name: string | null
  status: number
}

interface LogEntry {
  id: number
  header: string | null
  log_date: string | null
  scheduled_date: string | null
  log_type: string | null
  user: string | null
  status: number
  urgent: boolean
  completed: boolean
}

interface Props {
  customer: Customer
  contact: Contact
  logs: LogEntry[]
  can_add_schedule: boolean
}

const MONTHS = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

function formatBirthday(month: number | null, day: number | null): string {
  if (!month) return ""
  const monthName = MONTHS[month] || ""
  return day ? `${monthName} ${day}` : monthName
}

export default function Show({
  customer,
  contact,
  logs,
  can_add_schedule,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Contacts", href: `/customers/${customer.id}/contacts` },
    {
      title: contact.display_name,
      href: `/customers/${customer.id}/contacts/${contact.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this contact?")) {
      router.delete(`/customers/${customer.id}/contacts/${contact.id}`)
    }
  }

  const cityStateZip = [
    contact.city,
    contact.state
      ? `${contact.state} ${contact.zip || ""}`.trim()
      : contact.zip,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${contact.display_name} - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{contact.display_name}</h1>
          <div className="flex gap-2">
            <Link
              href={`/customers/${customer.id}/contacts/${contact.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Contact Details */}
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Contact Details</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              {contact.salutation && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Salutation</dt>
                  <dd>{contact.salutation}</dd>
                </>
              )}
              <dt className="text-hpi-med-blue font-medium">Name</dt>
              <dd>{contact.display_name}</dd>
              {contact.department && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Department</dt>
                  <dd>{contact.department}</dd>
                </>
              )}
              {contact.sales_rep_name && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Sales Rep</dt>
                  <dd>{contact.sales_rep_name}</dd>
                </>
              )}
              <dt className="text-hpi-med-blue font-medium">Status</dt>
              <dd>
                {contact.status === 90 ? (
                  <span className="bg-hpi-red/10 text-hpi-red rounded px-2 py-0.5 text-xs">
                    Do Not Contact
                  </span>
                ) : (
                  <span className="bg-hpi-dark-green/10 text-hpi-dark-green rounded px-2 py-0.5 text-xs">
                    Active
                  </span>
                )}
              </dd>
            </dl>
          </div>

          {/* Contact Info */}
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Contact Info</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              {contact.phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Office</dt>
                  <dd>{contact.phone}</dd>
                </>
              )}
              {contact.cell_phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Cell</dt>
                  <dd>{contact.cell_phone}</dd>
                </>
              )}
              {contact.home_phone && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Home</dt>
                  <dd>{contact.home_phone}</dd>
                </>
              )}
              {contact.fax && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Fax</dt>
                  <dd>{contact.fax}</dd>
                </>
              )}
              {contact.email && (
                <>
                  <dt className="text-hpi-med-blue font-medium">Email</dt>
                  <dd>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {contact.email}
                    </a>
                  </dd>
                </>
              )}
              {!contact.phone &&
                !contact.cell_phone &&
                !contact.home_phone &&
                !contact.fax &&
                !contact.email && (
                  <dd className="text-hpi-med-gray col-span-2">
                    No contact info on file.
                  </dd>
                )}
            </dl>
          </div>

          {/* Address */}
          {(contact.address || cityStateZip) && (
            <div className="border-hpi-light-gray rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Address</h2>
              {contact.address && <p>{contact.address}</p>}
              {contact.address2 && <p>{contact.address2}</p>}
              {cityStateZip && <p>{cityStateZip}</p>}
            </div>
          )}

          {/* Personal */}
          {(contact.birth_month ||
            contact.family_info ||
            contact.notes ||
            contact.send_birthday_card) && (
            <div className="border-hpi-light-gray rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Personal</h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                {contact.birth_month && (
                  <>
                    <dt className="text-hpi-med-blue font-medium">Birthday</dt>
                    <dd>
                      {formatBirthday(contact.birth_month, contact.birth_day)}
                      {contact.send_birthday_card && (
                        <span className="bg-hpi-dark-green/10 text-hpi-dark-green ml-2 rounded px-2 py-0.5 text-xs">
                          Send Card
                        </span>
                      )}
                    </dd>
                  </>
                )}
                {contact.family_info && (
                  <>
                    <dt className="text-hpi-med-blue font-medium">Family</dt>
                    <dd className="whitespace-pre-wrap">
                      {contact.family_info}
                    </dd>
                  </>
                )}
                {contact.notes && (
                  <>
                    <dt className="text-hpi-med-blue font-medium">Notes</dt>
                    <dd className="whitespace-pre-wrap">{contact.notes}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </div>

        {/* Logs Section */}
        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Logs</h2>
            <DropdownMenu>
              <DropdownMenuTrigger className="bg-hpi-dark-blue hover:bg-hpi-blue flex items-center gap-1.5 rounded px-4 py-2 text-white outline-none">
                <CirclePlus className="h-4 w-4" />
                <span>Add</span>
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <a
                    href={`/customers/${customer.id}/logs/new?type=log_note&customer_user_id=${contact.id}`}
                    className="cursor-pointer"
                  >
                    Log Note
                  </a>
                </DropdownMenuItem>
                {can_add_schedule && (
                  <DropdownMenuItem asChild>
                    <a
                      href={`/customers/${customer.id}/schedules/new?customer_user_id=${contact.id}`}
                      className="cursor-pointer"
                    >
                      Meeting
                    </a>
                  </DropdownMenuItem>
                )}
                {contact.email && (
                  <DropdownMenuItem asChild>
                    <a
                      href={`/customers/${customer.id}/emails?contact_id=${contact.id}`}
                      className="cursor-pointer"
                    >
                      Send Resources
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {logs.length > 0 ? (
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Header
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Date
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Type
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Assigned To
                  </th>
                  <th className="border-hpi-light-gray border px-4 py-2 text-left">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-hpi-pale-blue">
                    <td className="border-hpi-light-gray border px-4 py-2">
                      <Link
                        href={`/customers/${customer.id}/logs/${log.id}`}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {log.header || "(No subject)"}
                      </Link>
                      {log.urgent && (
                        <span className="bg-hpi-red/20 text-hpi-red ml-2 rounded px-2 py-0.5 text-xs">
                          Urgent
                        </span>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {log.log_date ? formatDate(log.log_date) : ""}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {log.log_type}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {log.user}
                    </td>
                    <td className="border-hpi-light-gray border px-4 py-2">
                      {log.completed ? (
                        <span className="bg-hpi-dark-green/10 text-hpi-dark-green rounded px-2 py-0.5 text-xs">
                          Complete
                        </span>
                      ) : (
                        <span className="bg-hpi-yellow/20 text-hpi-yellow rounded px-2 py-0.5 text-xs">
                          Open
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-hpi-med-gray">No logs for this contact.</p>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/customers/${customer.id}/contacts`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Contacts
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
